"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";

const manufacturingLinks = [
  { href: "/casting", label: "Casting" },
  { href: "/molding", label: "Molding" },
  { href: "/stamping-services", label: "Stamping" },
  { href: "/cnc", label: "CNC Machining" },
  { href: "/tube-bending", label: "Tube Bending" },
  { href: "/forging", label: "Forging" },
  { href: "/assembly-fabrication", label: "Assembly and Fabrication" },
  { href: "/finishing", label: "Finishing" },
];

const servicesLinks = [
  { href: "/cnc-machining-company", label: "CNC Machining" },
  { href: "/global-sourcing", label: "Global Sourcing" },
  { href: "/global-parts-supplier", label: "Global Parts Supplier" },
  { href: "/supply-chain-solutions", label: "Supply Chain Solutions" },
  { href: "/supply-chain-management-services", label: "Supply Chain Management" },
  { href: "/global-manufacturing-company", label: "Global Operations" },
  { href: "/offshore-manufacturing-company", label: "Offshore Manufacturing" },
  { href: "/machined-parts-manufacturer", label: "Machined Parts Manufacturer" },
  { href: "/engineering-parts-manufacturer", label: "Engineering Parts Manufacturing" },
  { href: "/inventory-management-systems", label: "Inventory Management Systems" },
  { href: "/contract-manufacturer-usa", label: "Contract Manufacturing (USA)" },
];

const links = [
  { href: "/about", label: "About Us" },
  { href: "/processes", label: "Quality" },
  { href: "/industries", label: "Industries" },
  { href: "/blogs", label: "Blogs" },
  { href: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [isManufacturingOpen, setIsManufacturingOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileManufacturingOpen, setMobileManufacturingOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  /* Scroll detection */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ESC closes mobile menu */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Disable body scroll when mobile menu open */
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
  }, [mobileOpen]);

  return (
    <>
      {/* NAVBAR */}
      <nav
        className={`fixed inset-x-0 top-0 z-50 bg-white transition-all ${
          scrolled
            ? "border-b border-black/10 shadow-sm"
            : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto h-14 md:h-16 px-4 sm:px-6 md:px-8">
          <div className="h-full flex items-center justify-between gap-4">
            {/* LOGO */}
            <Link
              href="/"
              className="flex items-center gap-3 shrink-0"
              onClick={() => setMobileOpen(false)}
            >
              <Image
                src="/mmi-logo.png"
                alt="MMI"
                width={60}
                height={60}
                priority
                className="h-12 w-12 md:h-14 md:w-14"
              />

              {/* Logo Text */}
              <span className="block leading-tight">
                <span className="block text-[11px] sm:text-[13px] md:text-[14px] font-semibold tracking-wide">
                  MICHIGAN MANUFACTURING
                </span>
                <span className="block text-[11px] sm:text-[13px] md:text-[14px] font-semibold tracking-wide">
                  INTERNATIONAL
                </span>
              </span>
            </Link>

            {/* DESKTOP MENU */}
            <ul className="hidden md:flex items-center gap-7 text-[14.5px] font-medium text-black/80">
              <li>
                <DropdownMenu
                  open={isManufacturingOpen}
                  onOpenChange={setIsManufacturingOpen}
                  modal={false}
                >
                  <DropdownMenuTrigger className="flex items-center gap-1 hover:text-black">
                    Manufacturing Processes
                    <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="start"
                    className="w-60"
                    onMouseEnter={() => setIsManufacturingOpen(true)}
                    onMouseLeave={() => setIsManufacturingOpen(false)}
                  >
                    {manufacturingLinks.map((link) => (
                      <DropdownMenuItem key={link.href} asChild>
                        <Link href={link.href}>{link.label}</Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>

              <li>
                <div
                  className="flex items-center gap-1"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <Link href="/services" className="hover:text-black">
                    Services
                  </Link>
                  <DropdownMenu
                    open={isServicesOpen}
                    onOpenChange={setIsServicesOpen}
                    modal={false}
                  >
                    <DropdownMenuTrigger
                      aria-label="Toggle services dropdown"
                      className="flex items-center hover:text-black"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="start"
                      className="w-72"
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      {servicesLinks.map((link) => (
                        <DropdownMenuItem key={link.href} asChild>
                          <Link href={link.href}>{link.label}</Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </li>

              {links.map((l) => (
                <li key={l.href}>
                  <Link href={l.href} className="hover:text-black">
                    {l.label}
                  </Link>
                </li>
              ))}

              <li>
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-1 hover:text-black">
                    Resources
                    <ChevronDown className="h-4 w-4" />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="start"
                    sideOffset={2}
                    className="w-52 p-1"
                  >
                    <DropdownMenuItem asChild>
                      <a
                        href="https://michmechdata.com/regist.php"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Customer Login
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            </ul>

            {/* MOBILE TOGGLE */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {!mobileOpen ? (
                <div className="flex flex-col justify-between w-5 h-3.5">
                  <span className="h-[2px] bg-black rounded-full" />
                  <span className="h-[2px] bg-black rounded-full" />
                </div>
              ) : (
                <div className="flex gap-1">
                  <span className="w-[2px] h-5 bg-black rounded-full" />
                  <span className="w-[2px] h-5 bg-black rounded-full" />
                </div>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU + BACKDROP */}
      {mobileOpen && (
        <>
          {/* Backdrop (click to close) */}
          <div
            className="fixed inset-0 z-40 bg-black/10 backdrop-blur-[2px]"
            onClick={() => setMobileOpen(false)}
          />

          {/* Mobile Menu Panel */}
          <div className="fixed top-14 left-0 right-0 z-50 bg-white shadow-lg md:hidden">
            <div className="px-6 py-6 space-y-5">
              {/* Manufacturing Dropdown */}
              <button
                className="w-full flex justify-between items-center text-lg font-medium"
                onClick={() => setMobileManufacturingOpen((prev) => !prev)}
              >
                Manufacturing Processes
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${
                    mobileManufacturingOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {mobileManufacturingOpen && (
                <div className="pl-4 space-y-3">
                  {manufacturingLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="block text-[15px] text-black/80"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}

              {/* Services Link + Dropdown */}
              <div>
                <div className="w-full flex justify-between items-center text-lg font-medium">
                  <Link href="/services" onClick={() => setMobileOpen(false)}>
                    Services
                  </Link>
                  <button
                    type="button"
                    aria-label="Toggle services submenu"
                    aria-expanded={mobileServicesOpen}
                    onClick={() => setMobileServicesOpen((prev) => !prev)}
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${
                        mobileServicesOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {mobileServicesOpen && (
                  <div className="pl-4 space-y-3 mt-3">
                    {servicesLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block text-[15px] text-black/80"
                        onClick={() => {
                          setMobileOpen(false);
                          setMobileServicesOpen(false);
                        }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Other Links */}
              {links.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  className="block text-lg font-medium"
                  onClick={() => setMobileOpen(false)}
                >
                  {l.label}
                </Link>
              ))}
              {/* Resources Dropdown */}
              <button
                className="w-full flex justify-between items-center text-lg font-medium"
                onClick={() => setMobileResourcesOpen((prev) => !prev)}
              >
                Resources
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${
                    mobileResourcesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {mobileResourcesOpen && (
                <div className="pl-4 space-y-3">
                  <a
                    href="https://michmechdata.com/regist.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[15px] text-black/80"
                    onClick={() => setMobileOpen(false)}
                  >
                    Customer Login
                  </a>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* SPACER */}
      <div className="h-14 md:h-16" />
    </>
  );
}
